import { CdkScrollable } from "@angular/cdk/scrolling";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { CachedRepositoryService, FormMetadataService, UserProfileService } from "@tylertech/core-module";
import { ForgeButtonModule } from "@tylertech/forge-angular";
import { MetadataPanelComponent } from "@tylertech/metadata-module";
import { ClassMetadata, ProfileSettingViewModel, ProfileViewModel } from "@tylertech/tyler-api-types";
import { ChartManagerService } from "../../services";

export interface ChartDialogData
{
    readonly chartName: string;
    readonly settings: Array<ProfileSettingViewModel>;
}

@Component({
    selector: "chart-dialog-component",
    templateUrl: "./chartDialog.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        CdkScrollable,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        FormsModule,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        MetadataPanelComponent,
        ReactiveFormsModule,
    ]
})
export class ChartDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<ChartDialogComponent>>(MatDialogRef);
    private readonly data = inject<ChartDialogData>(MAT_DIALOG_DATA);
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $repositoryService = inject(CachedRepositoryService);
    private readonly $formMetadataService = inject(FormMetadataService);
    private readonly $chartManagerService = inject(ChartManagerService);

    metadata: ClassMetadata;

    model: any;

    isLoaded: boolean;

    get chartName(): string { return this.data.chartName; }

    private get originalSettings(): Array<Readonly<ProfileSettingViewModel>> { return this.data.settings; }

    private get newSettings(): Array<Readonly<ProfileSettingViewModel>>
    {
        const settings: Array<ProfileSettingViewModel> = this.originalSettings;
        settings.forEach(s => s.Value = this.model[s.Id]);
        return settings;
    }

    get visibleSettings(): Array<ProfileSettingViewModel>
    {
        return this.originalSettings.filter(f => f.Hidden !== true);
    }

    ngOnInit(): void
    {
        const self = this;
        const promises: Promise<any>[] = [];

        promises.push(this.$formMetadataService.GetForm("UserProfileFormMetadata", "$filter=SingleChartSettings eq true")
            .then(form =>
            {
                const metadata: ClassMetadata = form.Views[0]["Views"][1].Views.find(v => v.Title === "Smart Charts").ViewMetaData.Properties[0].ClassMetadata;
                metadata.Properties = metadata.Properties.filter(p => self.visibleSettings.map(x => x.Id).indexOf(p.Name) > -1);
                self.metadata = metadata;
            }));

        promises.push(this.$repositoryService.GetNew(
            {
                Item1: "Api.Core.Service.userprofile.UserProfileViewModel",
                Item2: JSON.stringify({ ConfirmationQuestions: {} })
            }, "$filter=SingleChartSettings eq true")
            .then(m =>
            {
                self.model = m["ProfileSettings"];
            }));

        Promise.all(promises).then(_ => self.isLoaded = true);
    }

    clearDefaults(): void
    {
        this.isLoaded = false;

        const parms: ProfileViewModel = {
            Settings: this.newSettings,
            FirstName: undefined,
            Id: undefined,
            LastName: undefined,
            Type: undefined,
            Username: undefined,
        };
        this.$userProfileService.Clear(parms).then(_ => this.done());
    }

    saveDefaults(): void
    {
        this.isLoaded = false;
        const parms: ProfileViewModel = {
            Settings: this.newSettings,
            FirstName: undefined,
            Id: undefined,
            LastName: undefined,
            Type: undefined,
            Username: undefined,
        };
        parms.Settings.forEach(setting =>
        {
            if (Array.isArray(setting.Value))
                setting.Value = setting.Value.join(",");
        });
        this.$userProfileService.Save(parms).then(_ => this.done());

    }

    done(): void
    {
        let settingsChanged = false;
        this.originalSettings.forEach((setting) =>
        {
            if (!settingsChanged && setting.Value !== this.model[setting.Id])
            {
                if (setting.Id.substring(setting.Id.length - 6) === "Layout")
                    this.$chartManagerService.fireRefreshChartLayout();
                settingsChanged = true;
            }
        });

        this.isLoaded = true;
        this.dialogRef.close(settingsChanged ? this.newSettings : this.originalSettings);
    }
}
